import type { CSSProperties } from 'react';
import { useSurveyI18n } from '../i18n';
import type { SurveyPayload } from '../report';
import { seasonsSpan, type Season } from '../seasons';
import { fmtDay, fmtDayRange } from '../utils';

/*
 * Campaign calendar (design, first card): one column per campaign day, a row
 * per season with its weeks as blocks — done in the season's colour, the
 * running week in U red, the ones to come dashed — the months above, the day
 * the next season starts marked below, and a line on today. Days between two
 * seasons (the game closed) are left empty.
 *
 * Always the whole campaign, whatever the period picker says: it is where the
 * reader sees which season and week the other cards are talking about.
 */

const DAY_MS = 86_400_000;
const dayIndex = (from: string, iso: string) => Math.round((Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);
const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

type Props = {
  campaign: SurveyPayload['campaign'];
  seasons: Season[];
  /** The last campaign day with figures — "today" for a live campaign. */
  today: string | null;
};

export function CampaignCalendar({ campaign, seasons, today }: Props) {
  const { t } = useSurveyI18n();
  const { start, end } = seasonsSpan(seasons, campaign);
  const days = Math.max(1, dayIndex(start, end) + 1);
  // Grid column 1 holds the season names; day i of the campaign is column i + 2.
  const col = (iso: string) => Math.min(days, Math.max(0, dayIndex(start, iso))) + 2;
  const span = (from: string, to: string) => `${col(from)} / span ${Math.max(1, dayIndex(from, to) + 1)}`;
  const ended = seasons.length > 0 && seasons.every((s) => s.status === 'done') && !campaign.live;
  // Between two seasons the campaign is not live (the game is closed), but today still has a place on it.
  const showToday = !ended && today != null && today >= start && today <= end;

  // The months the campaign touches, each over its own days.
  const months: Array<{ from: string; to: string }> = [];
  for (let d = start; d <= end; d = addDays(d, 1)) {
    const last = months[months.length - 1];
    if (last && last.from.slice(0, 7) === d.slice(0, 7)) last.to = d;
    else months.push({ from: d, to: d });
  }
  const monthName = (iso: string) => new Intl.DateTimeFormat(t.locale, { month: 'long', timeZone: 'UTC' }).format(new Date(`${iso}T12:00:00Z`));

  const grid: CSSProperties = {
    gridTemplateColumns: `150px repeat(${days}, minmax(0, 1fr))`,
    // Today's label, the months, a row per season, the season starts.
    gridTemplateRows: `20px 22px repeat(${seasons.length}, 44px) 30px`,
  };
  const seasonRow = (si: number) => si + 3;
  const markerRow = seasons.length + 3;

  return (
    <section id="calendar" className="su-card">
      <div className="su-daily-head">
        <h2>{t.calendarTitle}</h2>
        {showToday && today && <span className="su-note">{t.today(fmtDay(today, t.locale, 'long'))}</span>}
        {ended && <span className="su-note">{t.campaignEndedOn(fmtDay(end, t.locale, 'long'))}</span>}
      </div>
      <div className="su-scroll">
        <div className="su-cal" style={grid} role="group" aria-label={t.calendarLabel}>
          {months.map((m) => (
            <div key={m.from} className="su-cal-month" style={{ gridRow: 2, gridColumn: span(m.from, m.to) }}>
              {monthName(m.from)}
            </div>
          ))}

          {seasons.map((s, si) => (
            <div key={s.id} style={{ display: 'contents' }}>
              <div className="su-cal-season" style={{ gridRow: seasonRow(si), gridColumn: 1 }}>
                <strong>
                  <span className="su-dot" style={{ background: s.color }} />
                  {t.season(s.n)}
                </strong>
                <span>{fmtDayRange(s.start, s.end, t.locale)}</span>
              </div>
              {s.weeks.map((w, k) => {
                const dates = fmtDayRange(w.start, w.end, t.locale);
                return (
                  <div
                    key={w.week}
                    className={`su-cal-week ${w.status}`}
                    style={{ gridRow: seasonRow(si), gridColumn: span(w.start, w.end), background: w.status === 'done' ? s.color : undefined }}
                    title={`${t.season(s.n)} · ${t.week(k + 1)} · ${dates}`}
                  >
                    {t.weekShort(k + 1)}
                    <span>{dates}</span>
                  </div>
                );
              })}
            </div>
          ))}

          {seasons.slice(1).map((s) => (
            <div key={s.id} className="su-cal-handover" style={{ gridRow: markerRow, gridColumn: `${col(s.start)} / span 1` }}>
              {t.seasonStarts(fmtDay(s.start, t.locale, 'dayMonth'), s.n)}
            </div>
          ))}

          {showToday && today && (
            <div
              className={`su-cal-today${dayIndex(start, today) > days * 0.85 ? ' flip' : ''}`}
              style={{ gridRow: `1 / ${markerRow}`, gridColumn: `${col(today)} / span 1` }}
            >
              <span>{t.todayShort(fmtDay(today, t.locale, 'dayMonth'))}</span>
            </div>
          )}
        </div>
      </div>
      <div className="su-legend">
        {seasons.map((s) => (
          <span key={s.id}>
            <span className="su-swatch" style={{ background: s.color }} />
            {t.weekDone(s.n)}
          </span>
        ))}
        <span>
          <span className="su-swatch" style={{ background: 'var(--su-red)' }} />
          {t.weekInProgress}
        </span>
        <span>
          <span className="su-swatch upcoming" />
          {t.upcoming}
        </span>
      </div>
    </section>
  );
}
